import { createFileRoute, Link } from '@tanstack/react-router';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/technology')({
  component: TechnologyPage,
});

const EXPERIENCE_START = 2000;
const yearsExperience = new Date().getFullYear() - EXPERIENCE_START;

const stack = [
  {
    title: 'Hosting',
    body: 'Cloudflare Workers. D1 for relational data. R2 for object storage. KV for short-lived caches. Durable Objects for serialised booking writes. Browser Rendering for automated screenshots.',
  },
  {
    title: 'Languages',
    body: 'TypeScript throughout — apps and packages. No JavaScript files outside generated output.',
  },
  {
    title: 'Web framework',
    body: 'TanStack family — Router, Query, Form, Table. React 19.',
  },
  {
    title: 'UI',
    body: 'Tailwind CSS plus shadcn/ui primitives, copied in (not a runtime dependency). Lucide icons.',
  },
  {
    title: 'Auth',
    body: 'Better Auth handles authentication. Authorisation lives in the domain model — Better Auth answers "who is signed in?", the domain answers "what can they do in this business?"',
  },
  {
    title: 'AI',
    body: 'Open-source models served via Cloudflare Workers AI. No proprietary AI providers baked in.',
  },
  {
    title: 'Notifications',
    body: 'Interface-driven. Email and SMS go through providers abstracted behind a Notifier interface so providers can be swapped without touching the domain.',
  },
  {
    title: 'Business routing',
    body: 'Each business has its own booking page at app.bookaburra.com/<slug>. Resolution is a single helper called at the edge of every public Worker route.',
  },
];

const guarantees: { title: string; body: React.ReactNode }[] = [
  {
    title: 'UTC at rest, business-local at the surface',
    body: <>Time is stored in UTC; every booking time rendered uses the business&rsquo;s configured timezone.</>,
  },
  {
    title: 'Money is integers, never floats',
    body: <>All monetary values are stored as the smallest currency unit. No rounding bugs in invoices.</>,
  },
  {
    title: 'Business data isolation',
    body: (
      <>
        Every database query goes through a business-scoped repository that takes
        the business id in its constructor. There is no hand-rolled{' '}
        <code className="rounded bg-brand-cream-shadow px-1.5 py-0.5 text-sm">
          WHERE business_id = ?
        </code>
        .
      </>
    ),
  },
  {
    title: 'Booking writes are serialised per business',
    body: (
      <>
        A BookingCoordinator Durable Object owns the write path, so
        double-bookings are not just unlikely — they are architecturally
        impossible.
      </>
    ),
  },
  {
    title: 'Domain logic is pure',
    body: (
      <>
        The allocator and reallocation engine live in a package that doesn&rsquo;t
        import the Workers runtime, D1, or any I/O. It runs under a standard test
        runner with no infrastructure setup.
      </>
    ),
  },
];

function TechnologyPage() {
  usePageTitle('Technology stack');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Technology
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl lg:text-[4rem]">
            Built on best-of-breed infrastructure and frameworks.
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            Bookaburra is designed and architected by{' '}
            <a
              href="https://www.linkedin.com/in/timgriffin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-sage underline-offset-2 hover:underline"
            >
              Tim Griffin
            </a>
            {' '}— one developer with {yearsExperience} years of ecommerce
            experience, built with the assistance of AI coding tools.
          </p>
          <div className="mt-8 max-w-2xl rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-6 text-base text-brand-ink-soft/80 leading-relaxed">
            <p>
              In an era where AI-generated software is common, we think it
              matters that every architectural decision is made by a human who
              has hit those failure modes in production before. The guardrails
              described on this page exist because the person who designed them
              learned why they matter the hard way — not because a language
              model suggested them.
            </p>
            <p className="mt-3">
              AI does the typing. Experience decides what gets typed.
            </p>
          </div>
        </div>
      </section>

      {/* STACK */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>The stack</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            Boring, proven, and entirely on Cloudflare.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            The stack was chosen for reliability and operational simplicity
            at small scale, not to chase trends.
          </p>
        </div>
        <dl className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2">
          {stack.map((item) => (
            <div key={item.title}>
              <dt className="text-base font-semibold text-brand-ink-soft">
                {item.title}
              </dt>
              <dd className="mt-2 text-sm leading-relaxed text-brand-ink-soft/75">
                {item.body}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* GUARANTEES */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>Architectural guardrails</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            Guardrails for the mistakes that hurt clients.
          </h2>
        </div>
        <ul className="mt-12 grid gap-5 sm:grid-cols-2">
          {guarantees.map((g) => (
            <li
              key={g.title}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream-shadow p-7"
            >
              <h3 className="text-lg font-semibold text-brand-ink-soft">
                {g.title}
              </h3>
              <div className="mt-3 text-base leading-relaxed text-brand-ink-soft/80">
                {g.body}
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* DESIGN NOTES */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>Design notes</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            Read the decisions, not just the stack.
          </h2>
          <p className="mt-5 text-lg text-brand-ink-soft/75">
            We publish a curated set of architecture decision records
            (ADRs) covering the calls that have real trade-offs &mdash;
            voice and chat intent matching, how the voice flow hands off
            to the booking form, the cost controls that keep voice
            affordable. Context, decision, trade-offs we accepted, and
            the alternatives we considered.
          </p>
          <div className="mt-8">
            <Link
              to="/adrs"
              className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-3 text-sm font-semibold text-brand-cream transition hover:bg-brand-ink/90"
            >
              Read the design notes &rarr;
            </Link>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-cream">
        <div className="mx-auto max-w-6xl px-6 py-20 sm:px-12 sm:py-24">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl">
            Need more before you recommend us?
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-brand-cream/80">
            We&rsquo;ll publish the API reference and integration guides once
            we&rsquo;re out of pre-launch. If you want a peek before then,{' '}
            <Link
              to="/contact"
              className="font-medium underline-offset-4 hover:underline"
            >
              get in touch
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  );
}
